import { FunctionComponent, useState } from "react";
import { TFunction } from "i18next";
import { faCheck, faChevronDown, faChevronUp, faCopy } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { copyText } from "@/utils/copyText";

interface ErrorJsonViewerProps {
	/** The raw persisted schedule.error JSON string. */
	rawError: string;
	t: TFunction;
}

const COPIED_FEEDBACK_MS = 1500;

/** Pretty-print the stored JSON; fall back to the raw string if it isn't valid JSON. */
const prettyPrint = (rawError: string): string => {
	try {
		return JSON.stringify(JSON.parse(rawError), null, 2);
	} catch {
		return rawError;
	}
};

/**
 * Collapsible viewer for the full persisted error payload, with a copy-to-clipboard
 * button. Used per failed step in the support dashboard's Delivery Errors panel so
 * support can inspect / copy the raw JSON behind the summarized reason.
 */
export const ErrorJsonViewer: FunctionComponent<ErrorJsonViewerProps> = ({ rawError, t }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isCopied, setIsCopied] = useState(false);

	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		void copyText(prettyPrint(rawError)).then((copied) => {
			if (!copied) {
				return;
			}

			setIsCopied(true);
			setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
		});
	};

	const toggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsOpen((open) => !open);
	};

	return (
		<div className="mt-1">
			<div className="flex flex-wrap items-center gap-3">
				<button
					className="inline-flex items-center gap-1 text-[11px] font-medium text-text-meta hover:text-text"
					type="button"
					onClick={toggle}
				>
					<Icon icon={isOpen ? faChevronUp : faChevronDown} />
					{isOpen
						? t("supportDashboard.deliveryErrors.hideJson")
						: t("supportDashboard.deliveryErrors.showJson")}
				</button>
				<button
					className={classNames(
						"inline-flex items-center gap-1 text-[11px] font-medium",
						isCopied ? "text-status-success" : "text-text-meta hover:text-text"
					)}
					type="button"
					onClick={handleCopy}
				>
					<Icon icon={isCopied ? faCheck : faCopy} />
					{isCopied
						? t("supportDashboard.deliveryErrors.copied")
						: t("supportDashboard.deliveryErrors.copyJson")}
				</button>
			</div>
			{isOpen && (
				<pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-inactive-item p-3 text-[11px] leading-relaxed text-text-meta">
					{prettyPrint(rawError)}
				</pre>
			)}
		</div>
	);
};
